import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { UserService } from "../../services/users/userService";
import { FournisseurService } from "../../services/fournisseurs/fournisseurService";
import { AuthService } from "../../services/auth/authService";
import { Lock, CreditCard, FileText, CheckCircle, Clock, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { getCurrentUser, logout } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";

export const FournisseurOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const navigate = useNavigate();

  const handlePasswordSubmit = async (data: any) => {
    if (data.new_password !== data.confirm_password) {
      toast.error(t('auth.register.passwordMismatch'));
      return;
    }
    try {
      setLoading(true);
      await AuthService.changePassword({
        current_password: data.current_password,
        new_password: data.new_password,
        confirm_password: data.confirm_password
      });
      toast.success(t('onboarding.security.success'));
      setStep(2);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('onboarding.security.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleIbanSubmit = async (data: any) => {
    try {
      setLoading(true);
      await AuthService.updateProfile({ iban: data.iban });
      toast.success(t('onboarding.finance.success'));
      setStep(3);
    } catch (error: any) {
      const ibanError = error.response?.data?.iban;
      const errMsg = Array.isArray(ibanError) ? ibanError[0] : (typeof ibanError === 'string' ? ibanError : t('onboarding.finance.error'));
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("type_document", type);
    formData.append("fichier", file);

    try {
      setLoading(true);
      await FournisseurService.uploadDocument(formData);
      toast.success(t('onboarding.documents.uploadSuccess', { type }));
    } catch (error) {
       toast.error(t('onboarding.documents.uploadError'));
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = async () => {
     try {
       setLoading(true);
       await AuthService.updateProfile({ preferences: { onboarding_completed: true } });
       await dispatch(getCurrentUser() as any);
       toast.success(t('onboarding.documents.success'));
     } catch (error) {
       console.error("Erreur lors de la finalisation", error);
       toast.error(t('common.errorGroup'));
     } finally {
       setLoading(false);
     }
  };

  const handleLogout = () => {
      dispatch(logout() as any);
      navigate("/login");
  };

  if (user?.profile?.preferences?.onboarding_completed && !user?.is_verified) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-10 px-4 shadow sm:rounded-lg sm:px-10 text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
                        <Clock className="h-8 w-8 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('onboarding.status.pendingTitle')}</h2>
                    <p className="text-gray-600 mb-8">
                        {t('onboarding.status.pendingDescription')}
                    </p>
                    <button
                        onClick={handleLogout}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('onboarding.status.logout')}
                    </button>
                </div>
            </div>
        </div>
      );
  }


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('dashboard.fournisseur.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('onboarding.security.subtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {/* Stepper */}
          <div className="mb-8">
             <div className="flex items-center justify-between">
                <div className={`flex flex-col items-center ${step >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
                   <div className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${step >= 1 ? 'border-primary-600 bg-primary-50' : 'border-gray-300'}`}>
                      <Lock className="h-4 w-4" />
                   </div>
                   <span className="text-xs mt-1 font-medium">{t('onboarding.steps.security')}</span>
                </div>
                <div className={`flex-1 h-0.5 mx-2 ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
                <div className={`flex flex-col items-center ${step >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
                   <div className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${step >= 2 ? 'border-primary-600 bg-primary-50' : 'border-gray-300'}`}>
                      <CreditCard className="h-4 w-4" />
                   </div>
                   <span className="text-xs mt-1 font-medium">{t('onboarding.steps.finance')}</span>
                </div>
                <div className={`flex-1 h-0.5 mx-2 ${step >= 3 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
                <div className={`flex flex-col items-center ${step >= 3 ? 'text-primary-600' : 'text-gray-400'}`}>
                   <div className={`rounded-full h-8 w-8 flex items-center justify-center border-2 ${step >= 3 ? 'border-primary-600 bg-primary-50' : 'border-gray-300'}`}>
                      <FileText className="h-4 w-4" />
                   </div>
                   <span className="text-xs mt-1 font-medium">{t('onboarding.steps.documents')}</span>
                </div>
             </div>
          </div>

          {step === 1 && (
            <form onSubmit={handleSubmit(handlePasswordSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('onboarding.security.currentPassword')}</label>
                <input
                  type="password"
                  {...register("current_password", { required: true })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('onboarding.security.newPassword')}</label>
                <input
                  type="password"
                  {...register("new_password", { required: true, minLength: 8 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('onboarding.security.confirmPassword')}</label>
                <input
                  type="password"
                  {...register("confirm_password", { required: true })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
              >
                {loading ? t('onboarding.common.saving') : t('onboarding.common.continue')}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit(handleIbanSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('onboarding.finance.iban')}</label>
                <input
                  type="text"
                  placeholder="FR76..."
                  {...register("iban", { required: true })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm uppercase font-mono"
                />
                <p className="mt-1 text-xs text-gray-500">{t('onboarding.finance.ibanHint')}</p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
              >
                {loading ? t('onboarding.common.saving') : t('onboarding.common.continue')}
              </button>
            </form>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.documents.kbis')}</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                   <div className="space-y-1 text-center">
                     <FileText className="mx-auto h-12 w-12 text-gray-400" />
                     <div className="flex text-sm text-gray-600 justify-center">
                       <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                         <span>{t('onboarding.documents.upload')}</span>
                         <input type="file" className="sr-only" accept=".pdf,.png,.jpg" onChange={(e) => handleFileChange(e, 'rccm')} disabled={loading} />
                       </label>
                     </div>
                   </div>
                 </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.documents.id')}</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                   <div className="space-y-1 text-center">
                     <FileText className="mx-auto h-12 w-12 text-gray-400" />
                     <div className="flex text-sm text-gray-600 justify-center">
                       <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                         <span>{t('onboarding.documents.upload')}</span>
                         <input type="file" className="sr-only" accept=".pdf,.png,.jpg" onChange={(e) => handleFileChange(e, 'identite')} disabled={loading} />
                       </label>
                     </div>
                   </div>
                 </div>
              </div>

              <button
                onClick={finishOnboarding}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none"
              >
                <CheckCircle className="h-4 w-4 mr-2" /> {t('onboarding.documents.submit')}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
